import { Button } from "components/Atoms/Button";
import { ValidationResult } from "formvalidation/hooks";
import React, { useContext, useEffect } from "react";
import { AdvancingFieldLabel } from "./AdvancingFieldLabel";
import { emptySubmitChangeFunc } from "./emptySubmitChangeFunc";
import { InPageEditableField } from "./InPageEditableField";
import { InPageEditingContext } from "./InPageEditingContext";
import { AdvancingProvideSectionField, InPageEditableSectionField } from "./types";

interface Props {
  field: AdvancingProvideSectionField
  onValueChanged: (key: string, value: any) => void
  onEditStarted: (key: string) => void
  validate: () => Promise<ValidationResult>
}

export const AdvancingProvideFieldWithLabel = ({ field, onValueChanged, onEditStarted, validate }: Props) => {
  const { editingField } = useContext(InPageEditingContext);
  const isEdited = editingField.fieldKey === field.key;

  useEffect(() => {
    if (isEdited) onEditStarted(field.key);
  }, [isEdited]);

  // Every change is passed up while typing, so saving the edit itself has nothing left to submit
  const editableField: InPageEditableSectionField = {
    ...field,
    valueClassName: field.formElementBehavior === "visibleMandatory" ? "advancing-mandatory-field" : undefined,
    inPageEdit: field.inPageEdit && {
      ...field.inPageEdit,
      type: "custom",
      submitChange: emptySubmitChangeFunc,
      render: options => field.inPageEdit.render({
        ...options,
        onChange: (value, submit) => {
          onValueChanged(field.key, value);
          options.onChange(value, submit);
        },
        // A click outside only closes the editor, Enter also sends the value
        enter: async value => {
          // Validates before closing, the inputs' validators are gone once the editor closes
          const validatorResult = await validate();
          onValueChanged(field.key, value);
          options.onChange(value, true);
          if (!validatorResult.hasErrors) field.onEnter(value);
        },
        cancel: () => {
          onValueChanged(field.key, editingField.initialValue);
          options.cancel();
        },
      }),
    },
  };

  return <>
    <AdvancingFieldLabel field={field} />
    <InPageEditableField compact={false} field={editableField} />
    <div>
      {field.button.isVisible && <Button type={field.button.type} iconRight={field.button.icon} loading={field.button.isLoading} onClick={async () => {
        const validatorResult = await validate();
        if (!validatorResult.hasErrors) field.button.onClick();
      }} />}
    </div>
  </>
}
